// Presentational metric card for WAF overview strips (dashboard, events, etc.).

import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";

import colors from "../../../../../../utils/colors";
import {
  certsHeroMetricCardSx,
  certsHeroMetricCardHighlightSx,
} from "../../../certs/certsLayout";

const ACCENT = colors.primary[300];
const GREEN = colors.green[300];

export const wafOverviewStatNumberSx = {
  fontSize: 20,
  fontWeight: 600,
  lineHeight: "24px",
  letterSpacing: "-0.01em",
  color: colors.neutral[1000],
  fontVariantNumeric: "tabular-nums",
};

export const wafOverviewStatUnitSx = {
  fontSize: 12,
  fontWeight: 500,
  lineHeight: "18px",
  color: colors.neutral[600],
};

export const wafOverviewSubSx = {
  fontSize: 12,
  lineHeight: "18px",
  color: colors.neutral[700],
  mt: 0.75,
  fontVariantNumeric: "tabular-nums",
};

export const wafOverviewLabelSx = {
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  color: ACCENT,
  lineHeight: 1.3,
};

/** Vertical gap between the label row and metric content below. */
export const wafOverviewLabelContentMt = 2.25;

export const wafMetricCardSx = {
  ...certsHeroMetricCardSx,
  className: "waf-overview-metric-card",
};

export const wafMetricCardHighlightSx = certsHeroMetricCardHighlightSx;

/** Responsive grid for overview metric cards (default: 1 → 2 → 4 → 5 columns). */
export const wafOverviewGridSx = {
  className: "waf-overview-grid",
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "1fr 1fr",
    md: "repeat(4, 1fr)",
    lg: "repeat(5, 1fr)",
  },
  gap: 2,
  alignItems: "stretch",
};

/** Grid columns sized to metric count so cards always span full width. */
export function wafOverviewGridSxForCount(count) {
  const n = Math.max(1, count);
  return {
    ...wafOverviewGridSx,
    width: "100%",
    gridTemplateColumns: {
      xs: "1fr",
      sm: n > 1 ? "repeat(2, minmax(0, 1fr))" : "1fr",
      md: `repeat(${Math.min(n, 4)}, minmax(0, 1fr))`,
      lg: `repeat(${n}, minmax(0, 1fr))`,
    },
  };
}

/** Icon size inside {@link WAFOverviewIconChip} — left-aligned with stat text below. */
export const WAF_OVERVIEW_ICON_SX = { fontSize: 16, display: "block" };

export function WAFOverviewIconChip({ children, variant = "neutral" }) {
  const isPrimary = variant === "primary";
  return (
    <Box sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      flexShrink: 0,
      color: isPrimary ? ACCENT : colors.neutral[600],
      "& .MuiSvgIcon-root": {
        display: "block",
        marginLeft: "-2px",
      },
    }}>
      {children}
    </Box>
  );
}

export function WAFOverviewStatNumber({ value, unit, sx }) {
  return (
    <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ minHeight: 28 }}>
      <Typography component="span" data-overview-metric sx={{ ...wafOverviewStatNumberSx, ...sx }}>{value}</Typography>
      {unit ? <Typography component="span" data-hero-stat-unit sx={wafOverviewStatUnitSx}>{unit}</Typography> : null}
    </Stack>
  );
}

const nfmt = (n) => (typeof n === "number" && isFinite(n) ? n.toLocaleString() : "—");

export function WAFOverviewDeltaPill({ deltaCount, deltaUp }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{
        mt: 1,
        display: "inline-flex",
        alignSelf: "flex-start",
        px: 1,
        py: 0.375,
        borderRadius: "6px",
        fontSize: 11.5,
        fontWeight: 600,
        lineHeight: 1.3,
        fontVariantNumeric: "tabular-nums",
        color: deltaUp ? ACCENT : GREEN,
        backgroundColor: deltaUp ? "rgba(99, 113, 247, 0.08)" : colors.green[10],
        border: `1px solid ${deltaUp ? "rgba(99, 113, 247, 0.18)" : "rgba(31, 193, 107, 0.2)"}`,
      }}
    >
      {deltaUp ? <TrendingUpIcon sx={{ fontSize: 14 }} /> : <TrendingDownIcon sx={{ fontSize: 14 }} />}
      {deltaCount > 0 ? "+" : ""}{nfmt(deltaCount)} vs. previous
    </Stack>
  );
}

function WAFOverviewMetricCardBody({ icon, label, children }) {
  return (
    <Stack sx={{ minWidth: 0 }}>
      {icon ? (
        <Stack direction="row" alignItems="center" spacing={1}>
          {icon}
          <Typography component="span" data-overview-label sx={wafOverviewLabelSx}>{label}</Typography>
        </Stack>
      ) : (
        <Typography component="span" data-overview-label sx={wafOverviewLabelSx}>{label}</Typography>
      )}
      <Box sx={{ minWidth: 0, mt: wafOverviewLabelContentMt }}>{children}</Box>
    </Stack>
  );
}

/** Single overview metric panel — compose into a 4- or 5-column grid on the parent. */
export default function WAFOverviewMetricCard({ icon, label, children, highlight = false, sx }) {
  return (
    <Box sx={{
      ...wafMetricCardSx,
      ...(highlight ? wafMetricCardHighlightSx : {}),
      ...sx,
    }}>
      <WAFOverviewMetricCardBody icon={icon} label={label}>{children}</WAFOverviewMetricCardBody>
    </Box>
  );
}
